import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Wifi, Cpu, ShieldCheck, Package as PackageIcon, ClipboardList, Wrench } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { findProduct } from "@/data/storeProducts";

export default function ProductDetails() {
  const { slug } = useParams();
  const product = findProduct(slug);

  if (!product) {
    return (
      <div className="bg-bone min-h-screen">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <p className="label-eyebrow mb-4">Not found</p>
          <h1 className="font-serif text-4xl md:text-5xl text-forest font-light">This product doesn't exist.</h1>
          <Link to="/store" className="mt-8 inline-block">
            <Button className="rounded-full bg-forest hover:bg-forest-hover text-bone px-8 py-6">
              Back to Store
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const Icon = product.icon;

  return (
    <div className="bg-bone min-h-screen">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20">
        <Link
          to="/store"
          className="inline-flex items-center gap-2 text-sm text-forest hover:text-forest-hover mb-8"
          data-testid="back-to-store"
        >
          <ArrowLeft size={16} /> Back to Store
        </Link>

        {/* Hero */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
          <div className={`md:col-span-5 rounded-3xl bg-gradient-to-br ${product.tint} aspect-square flex items-center justify-center border border-[#E2DFD8] relative`}>
            <Icon size={140} strokeWidth={1.1} className="text-forest/70" />
            <span className="absolute top-4 right-4 text-[10px] uppercase tracking-widest bg-bone/95 text-forest px-3 py-1.5 rounded-full font-mono border border-[#E2DFD8]">
              Coming Soon
            </span>
          </div>

          <div className="md:col-span-7">
            <p className="label-eyebrow mb-3">{product.categoryLabel}</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter leading-[0.98] text-forest">
              {product.name}
            </h1>
            <p className="mt-6 text-lg text-ink/70 leading-relaxed max-w-xl">{product.short}</p>
            <p className="mt-6 text-sm font-mono text-ink/55">{product.compatibility}</p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/store#enquiry" data-testid="detail-cta-enquiry">
                <Button className="rounded-full bg-forest hover:bg-forest-hover text-bone px-8 py-6">
                  Enquire about {product.name}
                </Button>
              </Link>
              <Link to="/store" data-testid="detail-cta-browse">
                <Button variant="outline" className="rounded-full border-forest text-forest hover:bg-forest hover:text-bone px-8 py-6">
                  Browse other devices
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Section title="Overview" icon={ClipboardList}>
            <p>{product.overview}</p>
          </Section>
          <Section title="Problem it solves" icon={CheckCircle2}>
            <p>{product.problem}</p>
          </Section>
          <Section title="Recommended installation area" icon={Wrench}>
            <p>{product.installation_area}</p>
          </Section>
          <Section title="NOA app connection" icon={Wifi}>
            <p>{product.app_connection}</p>
          </Section>
          <Section title="Hub requirement" icon={Cpu}>
            <p>{product.hub_requirement}</p>
          </Section>
          <Section title="Warranty" icon={ShieldCheck}>
            <p>{product.warranty}</p>
          </Section>
        </div>

        {/* Alerts + Package */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Section title="Supported alerts and automations" icon={CheckCircle2} full>
            <ul className="space-y-2">
              {product.alerts_automations.map((a) => (
                <li key={a} className="flex items-start gap-2">
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-forest" strokeWidth={1.5} />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </Section>
          <Section title="Package contents" icon={PackageIcon} full>
            <ul className="space-y-2">
              {product.package_contents.map((c) => (
                <li key={c} className="flex items-start gap-2">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-forest shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>

        {/* Specs table */}
        <div className="mt-6 surface-card p-8" data-testid="specs-table">
          <p className="label-eyebrow mb-3">Technical specifications</p>
          <h3 className="font-serif text-2xl text-ink mb-6">At a glance</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            {product.specs.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-6 py-3 border-b border-[#E2DFD8] text-sm">
                <dt className="text-ink/55">{k}</dt>
                <dd className="text-ink text-right font-medium">{v}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Installation */}
        <div className="mt-6 surface-card p-8">
          <p className="label-eyebrow mb-3">Installation</p>
          <p className="text-ink/70 leading-relaxed">{product.installation}</p>
        </div>

        {/* Compatibility disclaimer */}
        <p className="mt-10 text-xs text-ink/50 leading-relaxed max-w-3xl" data-testid="compatibility-disclaimer">
          Compatibility disclaimer — NOA devices work with the NOA app. Compatibility with third-party smart-home platforms is not guaranteed. Specifications, availability and hardware regionalisation may change before general release. Check the NOA app onboarding flow for the up-to-date device list at time of activation.
        </p>
      </div>

      <Footer />
    </div>
  );
}

function Section({ title, icon: Icon, children, full = false }) {
  return (
    <div className={`surface-card p-6 ${full ? "md:col-span-1" : ""}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} strokeWidth={1.5} className="text-forest" />
        <p className="label-eyebrow">{title}</p>
      </div>
      <div className="text-sm text-ink/70 leading-relaxed">{children}</div>
    </div>
  );
}
